import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Download } from "lucide-react";

export default function Reports() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [traffic, setTraffic] = useState([]);
  const [d, setD] = useState(null);

  useEffect(() => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/reports/traffic${p}`).then((r) => setTraffic(r.data));
    api.get(`/dashboard${p}`).then((r) => setD(r.data));
  }, [effectiveStructure]);

  const dl = async (kind, fmt) => {
    const params = new URLSearchParams({ kind, fmt });
    if (effectiveStructure) params.set("structure_id", effectiveStructure);
    const res = await api.get(`/reports/export?${params.toString()}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `goweb_${kind}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title={t("nav.reports")} action={
        <div className="flex flex-wrap gap-2">
          <button data-testid="export-sessions-csv" onClick={() => dl("sessions", "csv")} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-xs font-medium transition-colors"><Download className="h-3.5 w-3.5" /> {t("exp.sessions")} CSV</button>
          <button data-testid="export-sessions-pdf" onClick={() => dl("sessions", "pdf")} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-xs font-medium transition-colors"><Download className="h-3.5 w-3.5" /> {t("exp.sessions")} PDF</button>
          <button data-testid="export-accounting-csv" onClick={() => dl("accounting", "csv")} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-xs font-medium transition-colors"><Download className="h-3.5 w-3.5" /> {t("exp.accounting")} CSV</button>
          <button data-testid="export-accounting-pdf" onClick={() => dl("accounting", "pdf")} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600/20 text-blue-300 ring-1 ring-blue-600/30 hover:bg-blue-600/30 px-3 py-2 text-xs font-medium transition-colors"><Download className="h-3.5 w-3.5" /> {t("exp.accounting")} PDF</button>
        </div>
      } />
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {[[t("dash.reservations"), d.reservations_total], [t("dash.codes_active"), d.codes_active], [t("dash.codes_disabled"), d.codes_disabled], [t("dash.sessions_active"), d.sessions_active], [t("dash.auth_accept"), d.auth_accept], [t("dash.auth_reject"), d.auth_reject], ["Up (GB)", d.traffic_up_gb], ["Down (GB)", d.traffic_down_gb]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-[#1F2937] bg-[#111827] p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
              <div className="font-display text-2xl font-bold mt-1 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6">
        <h3 className="font-display font-semibold mb-4">{t("dash.traffic_chart")}</h3>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={traffic}>
            <defs>
              <linearGradient id="dl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.5} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
              <linearGradient id="ul" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} /><stop offset="100%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
            <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
            <YAxis stroke="#6B7280" fontSize={11} />
            <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="download" name={t("dash.download")} stroke="#2563EB" fill="url(#dl)" strokeWidth={2} />
            <Area type="monotone" dataKey="upload" name={t("dash.upload")} stroke="#06B6D4" fill="url(#ul)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
